import React from 'react'

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useWatch } from 'react-hook-form'

import FormAutoComplete from 'components/input/FormAutoComplete'
import FormSelect from 'components/input/FormSelect'
import FormTextField from 'components/input/FormTextField'
import DescriptionListTable from 'components/table/DescriptionListTable'
import { withForm } from 'decorators/withForm'
import { companyDataToServer } from 'models/company-model'
import {
  countries,
  employeesOptions,
  employeesRanges,
  getEmployeesRange,
  linkedinIndustries,
  naces,
  normalizeCompanyValue,
} from 'utils/data-change-requests'
import { removeEmpty } from 'utils/objects'

const config = [
  { title: 'Name', render: () => <FormTextField name="name" /> },
  { title: 'Number', render: () => <FormTextField name="number" /> },
  {
    title: 'Internet Provider (ISP)',
    render: () => (
      <FormSelect
        allowEmpty
        name="isIsp"
        options={[
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]}
      />
    ),
  },
  { title: 'City', render: () => <FormTextField name="address.city" /> },
  { title: 'Address', render: () => <FormTextField name="address.address" /> },
  { title: 'State', render: () => <FormTextField name="address.state" /> },
  { title: 'Zip Code', render: () => <FormTextField name="address.zipCode" /> },
  {
    title: 'Type',
    render: () => (
      <FormSelect
        allowEmpty
        name="address.type"
        options={[
          { value: 'hq', label: 'HQ' },
          { value: 'office', label: 'Office' },
        ]}
      />
    ),
  },
  {
    title: 'Phone country',
    render: () => (
      <FormAutoComplete label="Phone country" multiple={false} name="address.phoneNumber.country" options={countries} />
    ),
  },
  {
    title: 'Phone country code',
    render: () => <FormTextField name="address.phoneNumber.countryCode" type="number" />,
  },
  {
    title: 'Phone number',
    render: () => <FormTextField name="address.phoneNumber.number" type="number" />,
  },
  {
    title: 'Nace code (Industry)',
    render: () => <FormAutoComplete label="Nace code" multiple={false} name="naceCode" options={naces} />,
  },
  {
    title: 'LinkedIn Industry',
    render: () => (
      <FormAutoComplete
        label="LinkedIn industry"
        multiple={false}
        name="linkedInIndustryCode"
        options={linkedinIndustries}
      />
    ),
  },
  { title: 'Year founded', render: () => <FormTextField name="foundedAt" type="number" /> },
  { title: 'Description', render: () => <FormTextField multiline name="description" /> },
  {
    title: 'Financial Report year',
    render: () => <FormTextField name="financialReport.year" type="number" />,
  },
  {
    title: 'Financial Report currency',
    render: () => <FormTextField name="financialReport.currency" />,
  },
  {
    title: 'Financial Report revenue',
    render: () => <FormTextField name="financialReport.revenue" type="number" />,
  },
  {
    title: 'Tech',
    render: () => <FormTextField helperText="Enter comma-separated values!" name="tech" />,
  },
  {
    title: 'Employees year',
    render: () => <FormTextField name="employees.year" type="number" />,
  },
  {
    title: 'Employees',
    render: () => (
      <FormSelect
        allowEmpty={false}
        format={(value: any) => (value ? getEmployeesRange(value.from, value.to) : '')}
        name="employees"
        normalize={(value: any, previousValue: any) => ({
          ...previousValue,
          from: employeesRanges[value].from,
          to: employeesRanges[value].to,
        })}
        options={employeesOptions}
      />
    ),
  },
  { title: 'LinkedIn Url', render: () => <FormTextField name="socials.linkedIn" /> },
  { title: 'Twitter Url', render: () => <FormTextField name="socials.twitter" /> },
  { title: 'Crunchbase Url', render: () => <FormTextField name="socials.crunchbase" /> },
  { title: 'Facebook Url', render: () => <FormTextField name="socials.facebook" /> },
]

type EditChangeRequestProps = {
  handleSubmit: (...args: any[]) => any
  open: boolean
  onClose: (...args: any[]) => any
  onSubmit: (...args: any[]) => any
}

const EditChangeRequest = ({ handleSubmit, open, onClose, onSubmit }: EditChangeRequestProps) => {
  const formValues = useWatch()

  const submitEdit = async (values: any) => {
    const normalized = normalizeCompanyValue(values)
    // RHF materialises a parent object when it registers a nested path, so untouched groups arrive as {}.
    const serverData = removeEmpty(companyDataToServer(normalized))
    await onSubmit(serverData)
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <form onSubmit={handleSubmit(submitEdit)}>
        <DialogTitle>Edit change request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Editing the proposed values for this change request. Fields not included in the original proposal will
            appear empty.
          </DialogContentText>
          <DescriptionListTable config={config} data={formValues} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button color="primary" type="submit">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default withForm({
  form: 'editChangeRequest',
  enableReinitialize: true,
})(EditChangeRequest)
